import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";

import {
  normalizePermissionIds,
  userHasPermissionByName,
  type IPermissionCatalogItem,
} from "./permissionUtils";

import {
  PERMISSION_ACTION,
  PERMISSION_MODULE,
  modulePermissionName,
} from "./modulePermissions";

const SUPER_ADMIN_NAMES = new Set(["super admin", "superadmin"]);

export const isSuperAdminRole = (roleName?: string): boolean => {
  if (!roleName) return false;
  return SUPER_ADMIN_NAMES.has(roleName.toLowerCase());
};

export type PermissionCheckInput = {
  permissionName?: string;
  permissionId?: number;
  userPermissionIds?: number[];
  permissionCatalog?: IPermissionCatalogItem[];
  roleName?: string;
};

/**
 * 1. Find permission in catalog by seeder name (e.g. `Role_List`)
 * 2. Get its id
 * 3. Allow only if user has that id
 */
export const checkPermissionAccess = ({
  permissionName,
  permissionId,
  userPermissionIds = [],
  permissionCatalog = [],
  roleName,
}: PermissionCheckInput): boolean => {
  if (isSuperAdminRole(roleName)) return true;
  if (!permissionName && permissionId == null) return true;

  // Dashboard is always accessible for logged-in users
  if (
    permissionName ===
    modulePermissionName(PERMISSION_MODULE.DASHBOARD, PERMISSION_ACTION.LIST)
  ) {
    return true;
  }

  const normalizedUserIds = normalizePermissionIds(userPermissionIds);
  if (!normalizedUserIds.length) return false;

  if (permissionId != null) {
    return normalizedUserIds.includes(Number(permissionId));
  }

  if (permissionName) {
    return userHasPermissionByName(
      permissionName,
      permissionCatalog,
      normalizedUserIds
    );
  }

  return false;
};

export const useCheckPermission = () => {
  const {
    user,
    userPermissionIds,
    permissionCatalog,
    permissionCatalogLoading,
  } = useSelector((state: any) => state.auth);

  const roleName = user?.roleName as string | undefined;
  const isSuperAdmin = isSuperAdminRole(roleName);

  const normalizedUserIds = useMemo(
    () => normalizePermissionIds(userPermissionIds),
    [userPermissionIds]
  );

  const checkPermission = useCallback(
    (permissionName?: string, permissionId?: number): boolean =>
      checkPermissionAccess({
        permissionName,
        permissionId,
        userPermissionIds: normalizedUserIds,
        permissionCatalog,
        roleName,
      }),
    [normalizedUserIds, permissionCatalog, roleName]
  );

  return {
    checkPermission,
    isSuperAdmin,
    permissionCatalog,
    permissionCatalogLoading,
    userPermissionIds: normalizedUserIds,
  };
};
